import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { retrieveVehiclesByNumberPlate } from '../legacy/services/apiRouterService';
import { VehicleResponseProps } from '../../../../types/VehicleResponse';
import LicensePlateSearchInput from './LicensePlateSearchInput';
import useTracking from '../hooks/tracking/useTracking';
import regex from '../utils/regexUtils';
import { Text, Center, VStack } from '@wegroup/design-system';

interface Props {
  error?: string;
  onChange: (vehicle: VehicleResponseProps[], licensePlate: string) => void;
  onError?: () => void;
  isLoading: boolean;
  isFreemium?: boolean;
  hasError?: string;
  setIsLoading: (p: boolean) => void;
  isExistingCar?: boolean;
  disabled?: boolean;
  initialValue?: string;
  maxWidth?: string | number;
}

const LicensePlateLookup: React.FC<React.PropsWithChildren<Props>> = ({
  error,
  onChange,
  isLoading,
  hasError,
  setIsLoading,
  onError,
  disabled = false,
  initialValue,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [lookupError, setLookupError] = useState<string>();
  const [lookupWarning, setLookupWarning] = useState<string>();
  const [licensePlate, setLicensePlate] = useState<string | undefined>(
    initialValue,
  );
  const { trackEvent } = useTracking();

  const handleSearch = async (licensePlate?: string) => {
    // Init
    trackEvent('Lookup In Risk Analysis Car Search License Plate');
    if (disabled) return;

    // Formatting check
    if (!licensePlate?.match(regex.licensePlateNL))
      return setLookupWarning(t('RISK-ANALYSIS_LICENSE-PLATE_NOT-VALID'));
    setLookupWarning(undefined);
    setIsLoading(true);

    // API call
    const [resp, status] = await retrieveVehiclesByNumberPlate(
      licensePlate?.trim(),
      'NL',
    );
    if (status !== 200 || resp?.code > 201) {
      setIsLoading(false);
      setLookupError(resp.msg);
      onError && onError();
      return;
    }
    setLookupError(undefined);
    setIsLoading(false);
    onChange(resp?.items, licensePlate);
  };

  useEffect(() => {
    if (hasError) setLookupError(hasError);
  }, [hasError]);

  const handleLicensePlateChange = (value: string) => {
    lookupWarning && setLookupWarning(undefined); // Reset any error message
    setLicensePlate(value);
  };

  return (
    <>
      <LicensePlateSearchInput
        warning={lookupWarning}
        onSearch={handleSearch}
        disabled={disabled}
        isLoading={isLoading}
        licensePlate={licensePlate}
        setLicensePlate={handleLicensePlateChange}
        {...otherProps}
      />
      {lookupError && (
        <Center maxW="container.sm" mx="auto" flexDirection="column">
          <img
            src="https://files.wegroup.be/images/IllustrationVehicleNotFound.svg"
            alt="loop illustration"
          />
          <VStack spacing="3">
            <Text textStyle="largeBodyBold" fontSize="4xl">
              {lookupError}
            </Text>
            <Text textStyle="largeBody">
              {t('RISK-ANALYSIS_LICENSE-PLATE_NOT-FOUND')}
            </Text>
          </VStack>
        </Center>
      )}
    </>
  );
};

export default LicensePlateLookup;
